import { useEffect, useRef, useState, type CSSProperties } from "react";
import { ArrowIcon } from "../../components/ArrowIcon";
import { ConnectionDiagram } from "./ConnectionDiagram";
import { PreviewWindowHeader } from "./PreviewWindowHeader";
import { InstallCommand } from "./InstallCommand";
import "./ContCamPage.css";

const sections = [
  ["overview", "Overview"],
  ["effects", "Camera effects"],
  ["top-bar", "Desktop controls"],
  ["connection", "Connecting"],
  ["install", "Installation"],
  ["notes", "A few notes"],
] as const;

function CameraIcon({ size = 16 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true"><rect x="3.5" y="5.5" width="10" height="13" rx="2" /><path d="m13.5 9 6-3v12l-6-3" /></svg>;
}

function EffectIcon({ kind }: { kind: "blur" | "light" | "frame" }) {
  return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
    {kind === "blur" ? <><path d="M12 3a9 9 0 0 0 0 18V3Z" /><path d="M16 5h.01M20 8h.01M16 10h.01M20 13h.01M16 15h.01M17 19h.01" strokeWidth="2.5" strokeLinecap="round" /></> : kind === "light" ? <><circle cx="12" cy="12" r="4" /><path d="M12 2v2m0 16v2M2 12h2m16 0h2M5 5l1.5 1.5m11 11L19 19M5 19l1.5-1.5m11-11L19 5" /></> : <><path d="M8 3H3v5m13-5h5v5M3 16v5h5m13-5v5h-5" /><rect x="8" y="7" width="8" height="10" rx="3" /></>}
  </svg>;
}

export default function ContCamPage() {
  const [blur, setBlur] = useState(50);
  const [light, setLight] = useState(false);
  const [frame, setFrame] = useState(false);
  const [original, setOriginal] = useState(false);
  const [panelOpen, setPanelOpen] = useState(true);
  const [activeSection, setActiveSection] = useState("overview");
  const toolbarButtonRef = useRef<HTMLButtonElement>(null);
  const lastBlur = useRef(50);

  useEffect(() => {
    document.title = "ContCam — Tools by Yash Bindal";
    document.querySelector('meta[name="description"]')?.setAttribute("content", "An iPhone webcam for Windows and Linux, by Yash Bindal. Use your iPhone as a webcam, with background blur, studio light, and desktop controls.");
    const updateSection = () => {
      let current: string = "overview";
      for (const [id] of sections) {
        if ((document.getElementById(id)?.getBoundingClientRect().top ?? Infinity) <= 180) current = id;
      }
      setActiveSection(current);
    };
    window.addEventListener("scroll", updateSection, { passive: true });
    updateSection();
    if (window.location.hash) requestAnimationFrame(() => document.getElementById(window.location.hash.slice(1))?.scrollIntoView());
    return () => {
      window.removeEventListener("scroll", updateSection);
    };
  }, []);

  const updateBlur = (value: number) => {
    setBlur(value);
    if (value > 0) lastBlur.current = value;
    setOriginal(false);
  };

  const toggleLight = () => { setLight(value => !value); setOriginal(false); };
  const toggleFrame = () => { setFrame(value => !value); setOriginal(false); };

  const previewStyle = {
    "--demo-blur": `${original ? 0 : blur * 0.12}px`,
    "--demo-light": original || !light ? "1" : "1.18",
    "--demo-scale": original || !frame ? "1" : "1.15",
  } as CSSProperties;

  return (
    <main className="omacam-page">
      <a className="oc-skip" href="#overview">Skip to ContCam</a>
      <aside className="oc-rail">
        <a className="oc-back" href="/tools"><ArrowIcon direction="left" /><span>Tools</span></a>
        <nav aria-label="On this page">
          {sections.map(([id, label]) => <a key={id} href={`#${id}`} aria-current={activeSection === id ? "location" : undefined}>{label}</a>)}
        </nav>
        <a className="oc-author" href="/"><span>Yash Bindal</span><ArrowIcon direction="upRight" /></a>
      </aside>

      <article className="oc-article">
        <header id="overview" className="oc-intro">
          <h1 className="oc-product-title"><img src="/tools/contcam/contcam.svg" width="32" height="32" alt="" />ContCam</h1>
          <p className="oc-byline">A tool by <a href="/">Yash Bindal</a> <span aria-hidden="true">·</span> Private beta</p>
          <p>On a Mac, <a className="oc-inline-link" href="https://support.apple.com/102546" target="_blank" rel="noopener noreferrer">Continuity Camera</a> lets you use your iPhone as a webcam. ContCam brings a similar direct connection to Windows and Linux.</p>
          <p>Pair your iPhone, choose ContCam in your video-call app, and adjust blur, lighting, and framing from your desktop.</p>
          <a className="oc-install-link" href="#install"><span>Install ContCam</span><ArrowIcon direction="down" /></a>
        </header>

        <figure className="oc-camera-figure">
          <div className="oc-camera-demo">
            <PreviewWindowHeader />
            <div className="oc-camera-scene" style={previewStyle} role="group" aria-label={`Illustrated webcam preview. ${original ? "Original picture, without effects." : `Background blur ${blur} percent, studio light ${light ? "on" : "off"}, auto framing ${frame ? "on" : "off"}.`}`}>
              <img className="oc-room" src="/tools/contcam/demo-room.svg" width="1200" height="800" alt="" />
              <img className="oc-subject" src="/tools/contcam/demo-subject.webp" width="1024" height="1024" alt="" fetchPriority="high" />
              <button className="oc-compare" type="button" onClick={() => setOriginal(value => !value)}>{original ? "Show effects" : "Show original"}</button>
            </div>
            <div className="oc-demo-controls">
              <div className="oc-range-control">
                <label htmlFor="oc-blur"><EffectIcon kind="blur" /> Background blur <output htmlFor="oc-blur">{blur}%</output></label>
                <input id="oc-blur" type="range" min="0" max="100" step="1" value={blur} aria-valuetext={`${blur}% background blur`} onChange={event => updateBlur(Number(event.target.value))} />
              </div>
              <div className="oc-effect-buttons">
                <button type="button" aria-pressed={light} onClick={toggleLight}><EffectIcon kind="light" /> Studio light</button>
                <button type="button" aria-pressed={frame} onClick={toggleFrame}><EffectIcon kind="frame" /> Auto framing</button>
              </div>
            </div>
          </div>
          <figcaption>Try the controls. This is an illustration of the effects, not a live camera feed.</figcaption>
        </figure>

        <section id="effects">
          <h2>Camera effects</h2>
          <p>Background blur has a full 0–100% range. Keep a little of the room in view, or soften it all the way.</p>
          <p>Studio light brightens your face. Auto framing brings you closer. Use them together or keep the picture as it is.</p>
        </section>

        <section id="top-bar">
          <h2>Desktop controls</h2>
          <p>Adjust your picture from the ContCam desktop window on Windows and Linux. On Omarchy, the same controls are also available in the top bar. There’s no need to reach for your phone.</p>
          <figure>
            <div className="oc-desktop-demo" onKeyDown={event => {
              if (event.key === "Escape" && panelOpen) { setPanelOpen(false); toolbarButtonRef.current?.focus(); }
            }}>
              <div className="oc-desktop-bar">
                <span className="oc-workspaces" aria-hidden="true"><b>1</b><span>2</span><span>3</span></span>
                <span className="oc-bar-clock" aria-hidden="true">09:41</span>
                <button ref={toolbarButtonRef} className="oc-bar-button" type="button" aria-label="Camera controls" aria-expanded={panelOpen} aria-controls="oc-toolbar-panel" onClick={() => setPanelOpen(value => !value)}><CameraIcon /><span>ContCam</span><span aria-hidden="true">⌄</span></button>
              </div>
              <div className="oc-desktop-surface">
                <span className="oc-desktop-hint" aria-hidden="true">Your desktop.<br />Everything within reach.</span>
                {panelOpen && <div className="oc-toolbar-panel" id="oc-toolbar-panel" role="group" aria-label="Top bar camera effects">
                  <div className="oc-panel-heading"><span>Camera effects</span><small>ContCam</small></div>
                  <button className="oc-toggle-row" type="button" aria-pressed={blur > 0} onClick={() => updateBlur(blur > 0 ? 0 : lastBlur.current)}><EffectIcon kind="blur" /><span>Background blur</span><span className="oc-switch" aria-hidden="true" /></button>
                  <div className="oc-panel-range"><label className="oc-sr-only" htmlFor="oc-toolbar-blur">Top bar background blur</label><input id="oc-toolbar-blur" type="range" min="0" max="100" value={blur} aria-valuetext={`${blur}% background blur`} onChange={event => updateBlur(Number(event.target.value))} /><output htmlFor="oc-toolbar-blur">{blur}%</output></div>
                  <button className="oc-toggle-row" type="button" aria-pressed={light} onClick={toggleLight}><EffectIcon kind="light" /><span>Studio light</span><span className="oc-switch" aria-hidden="true" /></button>
                  <button className="oc-toggle-row" type="button" aria-pressed={frame} onClick={toggleFrame}><EffectIcon kind="frame" /><span>Auto framing</span><span className="oc-switch" aria-hidden="true" /></button>
                </div>}
              </div>
            </div>
            <figcaption>A preview of the top-bar controls. Changes also update the image above.</figcaption>
          </figure>
        </section>

        <section id="connection">
          <h2>Connecting</h2>
          <p>Scan the pairing code in the desktop app while both devices are on the same Wi-Fi network. USB support is also included, with real-device testing still in progress.</p>
          <p>The video travels directly between your iPhone and your computer. ContCam doesn’t send the feed through a cloud service.</p>
          <ConnectionDiagram />
        </section>

        <section id="install">
          <h2>Installation</h2>
          <p>You’ll need the desktop companion and the iPhone app. Existing TestFlight invitations still work; the ContCam replacement build is being prepared.</p>
          <h3>On Windows</h3>
          <p>ContCam targets Windows 11 x64 on Intel and AMD PCs. The installer includes the virtual camera. Choose <strong>ContCam Windows Virtual Camera</strong> in your call app and use your computer microphone.</p>
          <p>The current Windows build is an unsigned preview. Releases in the private source repository require GitHub access. A signed public download and in-app update channel are not available yet.</p>
          <p><a className="oc-inline-link" href="https://github.com/omayb/omacam/releases" target="_blank" rel="noopener noreferrer">View available releases</a></p>
          <h3>On Omarchy / Arch Linux</h3>
          <p className="oc-install-lead"><mark className="oc-marker">One command.</mark> Everything for Omarchy.</p>
          <p>The installer handles the packages, desktop app, virtual camera, and top-bar controls.</p>
          <InstallCommand tool="contcam" />
          <p>Then open ContCam and pair your iPhone. On Linux, choose the existing “OmaCam” virtual camera in your call app; its device name is retained for compatibility. Keep ContCam open and your iPhone unlocked while you use it.</p>
          <h3>On iPhone</h3>
          <p>The iPhone app is in private TestFlight beta. If you’ve received an invitation, open it on your iPhone. Older builds still show OmaCam; the ContCam build replaces that name and keeps your pairing. Public App Store availability is pending review.</p>
        </section>

        <section id="notes">
          <h2>A few notes</h2>
          <ul className="oc-notes">
            <li>Camera features are free. No subscription.</li>
            <li>ContCam is the new name for OmaCam, with the same direct pairing and on-device camera effects. Windows and Linux have different audio and installation requirements.</li>
            <li>Quality options include 1080p at 60 fps and up to 4K at 30 fps. Available settings depend on your iPhone and lens.</li>
            <li>This is a beta. Connection quality and compatibility with video-call apps can vary.</li>
          </ul>
          <p>ContCam started on Omarchy. The goal is the same on every supported desktop: open the app, connect your phone, and get on with your call.</p>
        </section>

        <p className="oc-small"><a href="/tools/contcam/support">Setup and support</a> · <a href="/tools/contcam/privacy">Privacy policy</a></p>
        <footer className="oc-footer"><a href="/tools"><ArrowIcon direction="left" /><span>All tools</span></a><a href="https://github.com/omayb/omacam" target="_blank" rel="noopener noreferrer"><span>GitHub</span><ArrowIcon direction="upRight" /></a></footer>
      </article>
    </main>
  );
}
